import React, { useState, useRef, useEffect, ReactNode } from 'react';
import Datetime from 'react-datetime';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import { KeyboardArrowDown } from '@mui/icons-material';
import ArrowDropUp from '@mui/icons-material/KeyboardArrowUp';
import Popover from '@mui/material/Popover';
import moment, { Moment } from 'moment';
import { withStyles, WithStyles } from '@mui/styles';
import {
  makeTodayMomentWithTz,
  convertDateToMomentWithTz,
  USER_DEVICE_ZONE,
} from '~/utils/datetime';
import { Icon } from 'bs-unified-ui';
import Button from '~/components/common/Button';
import { COLORS } from '~/themes/common';
import TextInput from '~/components/common/TextInput';
import 'react-datetime/css/react-datetime.css';

const themeStyle = (theme: any) => ({
  dateTimeContainer: {
    '& .rdtPicker': {
      border: 'none',
      boxShadow: 'unset',
      zIndex: '3 !important',
    },
  },
  pickupInput: {
    '& .MuiOutlinedInput-input': {
      'color': `#33363D !important`,
      'height': 35,
      'padding': 0,
      'fontSize': 14,
      'paddingLeft': 10,
      '&.Mui-disabled': {
        color: `${COLORS.gray.textGray} !important`,
      },
    },
  },
  bottomContainer: {
    marginTop: 30,
    display: 'flex',
    justifyContent: 'space-between',
  },
  leftItem: {
    marginRight: theme.spacing(1),
  },
  wrapper: {
    'fontSize': 14,
    'display': 'flex',
    '& .past': {
      '& .MuiOutlinedInput-root': {
        background: '#FFF6E9 0% 0% no-repeat padding-box',
      },
    },
    '& .future': {
      '& .MuiOutlinedInput-root': {
        background: '#E7F8F5 0% 0% no-repeat padding-box',
      },
    },
    '@media screen and (max-width: 1200px)': {
      '& .text-range': {
        display: 'none!important',
      },
    },
    '& #time-picker': {
      'height': 32,
      'width': '100%',
      'display': 'flex',
      '& .rdt': {
        width: '100%',
      },
      '@media screen and (max-width: 1024px)': {
        width: '120px',
      },
      '& .MuiInputBase-root': {
        '@media screen and (max-width: 1024px)': {
          'paddingRight': 5,
          '& input': {
            paddingRight: 0,
          },
        },
      },
    },
    '& .button-today': {
      'marginRight': 20,
      'marginLeft': 10,
      '@media screen and (max-width: 1024px)': {
        width: 65,
      },
    },
    '& .rangeDate.today-range': {
      opacity: 0,
    },
    '& .rangeDate': {
      'width': 125,
      'display': 'flex',
      'justifyContent': 'center',
      'alignItems': 'center',
      'backgroundColor': '#FFF6E9',
      'borderRadius': '17px',
      'marginRight': 20,
      '& .clock-range': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      '& .text-range': {
        font: 'normal normal normal 14px/17px Rubik',
        marginLeft: '10',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '123px',
        borderRadius: '17px',
      },
      '&.past': {
        color: '#FFA92B',
        backgroundColor: '#FFF6E9',
      },
      '&.future': {
        color: '#1CB69A',
        backgroundColor: '#E7F8F5',
      },
      '& path.past': {
        fill: '#FFA92B',
      },
      '& path.future': {
        fill: '#1CB69A',
      },
    },
  },

  wrapNextDate: {
    'display': 'flex',
    'justifyContent': 'flex-end',
    'alignItems': 'center',
    'flexGrow': 1,
    '& .arrow-next': {
      'width': '36px',
      'height': '36px',
      'borderRadius': '50%',
      'background': '#EDF1FB 0% 0% no-repeat padding-box',
      'display': 'flex',
      'justifyContent': 'center',
      'alignItems': 'center',
      'cursor': 'pointer',
      '& img': {
        width: '70%',
      },
    },
    '& .right-arrow': {
      transform: 'rotateY(180deg)',
      marginRight: 5,
    },
  },
  buttonClear: {
    marginRight: 6,
  },
  popover: {
    'marginTop': 5,
    'zIndex': 1300,
    'background': '#FFFFFF 0% 0% no-repeat padding-box',
    'boxShadow': '0px 2px 3px #D6DEF2',
    'borderRadius': 4,
    '& .rdtDays': {
      padding: 10,
    },
    '& .rdtDay': {
      borderRadius: '50%',
      textAlign: 'center',
      width: 35,
      height: 35,
      fontSize: 13,
    },
    '& .rdtDays tr .dow': {
      color: COLORS.gray.textGray2,
      fontWeight: 'normal',
      borderWidth: 0,
      textTransform: 'uppercase',
      fontSize: 13,
      paddingBottom: 15,
      letterSpacing: 0,
    },
    '& .rdtDays td.rdtDay:hover, .rdtDays td.rdtHour:hover, .rdtDays td.rdtMinute:hover, .rdtDays td.rdtSecond:hover, .rdtDays .rdtTimeToggle:hover':
      {
        background: `${COLORS.gray.disableGray1}`,
        cursor: 'pointer',
      },
    '& .rdtDays td.rdtToday:not(.rdtActive)': {
      'position': 'relative',
      '&::before': {
        content: '""',
        display: 'inline-block',
        borderLeft: '7px solid transparent',
        borderBottom: `7px solid ${COLORS.palette.primaryBlue}`,
        borderTopColor: 'rgba(0, 0, 0, 0.2)',
        position: 'absolute',
        bottom: '4px',
        right: '4px',
      },
    },
    '& .rdtDays td.rdtActive.rdtToday:before': {
      borderBottomColor: '#fff',
    },
    '& .rdtDay.rdtToday.rdtActive, .rdtDay.rdtActive, .rdtDay.rdtActive:hover':
      {
        backgroundColor: `${COLORS.palette.secondaryBlue6} !important`,
        color: `${COLORS.palette.primaryBlue} !important`,
        textDecoration: 'underline',
      },

    '& th.rdtSwitch': {
      minWidth: 50,
      color: `${COLORS.palette.primaryBlue} !important`,
      fontSize: '16px',
      fontWeight: '500',
      padding: '10px 0',
      cursor: 'pointer',
    },
    '& .rdtDays thead tr:first-of-type th': {
      cursor: 'pointer',
    },
    '& .rdtDays thead tr:first-of-type th:hover': {
      background: `${COLORS.gray.disableGray1}`,
    },
    '& .rdtYear': {
      width: 'calc(25% - 6px)',
    },
    '& .rdtDays .dow, .rdtDays th.rdtSwitch, .rdtDays th.rdtNext, .rdtDays th.rdtPrev, .rdtDays .rdtTimeToggle':
      {
        color: '#4D4F5C',
      },
    '& .rdtDays td.rdtDay.rdtDisabled, .rdtDays td.rdtDay.rdtDisabled:hover': {
      background: 'none',
      color: `${COLORS.gray.disableGray}`,
      cursor: 'not-allowed',
    },
    '& .rdtDays th.rdtNext, .rdtDays th.rdtPrev': {
      borderRadius: '25%',
      color: `${COLORS.palette.primaryBlue} !important`,
      fontSize: '21px',
    },
    '&  .rdtMonths': {
      'width': 300,
      '& table': {
        margin: 'auto',
        width: '100%',
        textAlign: 'center',
      },
      '&  thead th:hover': {
        background: `${COLORS.gray.disableGray1}`,
      },
    },
    '&  .rdtMonth': {
      width: 'calc(25% - 6px)',
      height: 50,
      cursor: 'pointer',
    },

    '& .rdtMonths .dow, .rdtDays th.rdtSwitch, .rdtDays th.rdtNext, .rdtDays th.rdtPrev, .rdtDays .rdtTimeToggle':
      {
        color: '#4D4F5C',
      },
    '& .rdtMonths th.rdtNext, .rdtMonths th.rdtPrev': {
      borderRadius: '25%',
      color: `${COLORS.palette.primaryBlue} !important`,
      fontSize: '21px',
      cursor: 'pointer',
      width: 37,
    },
    '& .rdtMonths td.rdtMonth.rdtDisabled, .rdtMonths td.rdtMonth.rdtDisabled:hover':
      {
        background: 'none',
        color: `${COLORS.gray.disableGray}`,
        cursor: 'not-allowed',
      },
    '& .rdtMonths td.rdtMonth.rdtActive, .rdtDays td.rdtMonth.rdtActive:hover':
      {
        backgroundColor: `${COLORS.palette.secondaryBlue12}`,
        color: `${COLORS.palette.primaryBlue}`,
        textShadow: '0 -1px 0 rgb(0 0 0 / 25%)',
      },
    '& .rdtYears': {
      'width': 300,
      'textAlign': 'center',
      '& table': {
        'width': '100%',
        '&  thead th:hover': {
          background: `${COLORS.gray.disableGray1}`,
        },
      },
    },
    '& .rdtYears th.rdtNext, .rdtYears th.rdtPrev': {
      borderRadius: '25%',
      color: `${COLORS.palette.primaryBlue} !important`,
      fontSize: '21px',
      cursor: 'pointer',
      width: 37,
    },
    '& .rdtYears td.rdtYear.rdtActive, .rdtYears td.rdtYear.rdtActive:hover': {
      backgroundColor: `${COLORS.palette.secondaryBlue12}`,
      color: `${COLORS.palette.primaryBlue}`,
      textShadow: '0 -1px 0 rgb(0 0 0 / 25%)',
    },
    '& .rdtYears td.rdtYear.rdtDisabled, .rdtYears td.rdtYear.rdtDisabled:hover':
      {
        background: 'none',
        color: `${COLORS.gray.disableGray}`,
        cursor: 'not-allowed',
      },
  },
});

interface DayPickerProps extends WithStyles<typeof themeStyle> {
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  leftItem?: ReactNode;
  isTodayButton?: boolean;
  isRangeTimeLabel?: boolean;
  isNextDay?: boolean;
  onClear?: (() => void) | null;
  disablePastDate?: boolean;
  disableFutureDate?: boolean;
  disableToday?: boolean;
  customDisable?: (current: Moment) => boolean;
  isDropDownIcon?: boolean;
  onBlur?: () => void;
  minimumDate?: string;
  timeZone?: string;
  error?: boolean;
  invalidDays?: string[];
  dataTestId?: string;
  paperDataTestId?: string;
  dateFormat?: string;
  [key: string]: any;
}

const DayPicker: React.FC<DayPickerProps> = ({
  classes,
  value: time = '',
  onChange: setTime = () => {},
  disabled = false,
  placeholder = '',
  className = '',
  leftItem = null,
  isTodayButton = false,
  isRangeTimeLabel = false,
  isNextDay = false,
  onClear = null,
  disablePastDate = false,
  disableFutureDate = false,
  disableToday = false,
  customDisable = () => true,
  isDropDownIcon = true,
  onBlur = () => {},
  minimumDate = '',
  timeZone = USER_DEVICE_ZONE,
  error = false,
  invalidDays = [],
  dataTestId = '',
  paperDataTestId = '',
  dateFormat = 'MM/DD/YYYY',
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const timePickerRef = useRef<HTMLDivElement>(null);
  const [isToday, setisToday] = useState(false);
  const [isPast, setisPast] = useState(false);

  const open = Boolean(anchorEl);

  useEffect(() => {
    const today = makeTodayMomentWithTz(timeZone).format(dateFormat);
    setisToday(today === time);
    setisPast(
      makeTodayMomentWithTz().diff(
        convertDateToMomentWithTz(time, dateFormat),
        'days',
      ) > 0,
    );
  }, []);

  useEffect(() => {
    const today = makeTodayMomentWithTz(timeZone).format(dateFormat);
    setisPast(
      makeTodayMomentWithTz().diff(
        convertDateToMomentWithTz(time, dateFormat),
        'days',
      ) > 0,
    );
    setisToday(today === time);
  }, [time, timeZone, dateFormat]);

  const handleChangeDate = (val: Moment) => {
    const convertVal = val.format(dateFormat);
    const today = makeTodayMomentWithTz(timeZone).format(dateFormat);
    setisToday(today === convertVal);
    setTime(convertVal);
    setAnchorEl(null);
    setisPast(moment().diff(moment(convertVal, dateFormat), 'days') > 0);
  };

  const yesterday = makeTodayMomentWithTz().subtract(1, 'day');
  const today = makeTodayMomentWithTz();

  const disableDate = (current: Moment) => {
    const currentDate = current.format(dateFormat);
    if (invalidDays?.length && invalidDays.includes(currentDate)) {
      return false;
    }
    if (minimumDate) {
      return current.isAfter(minimumDate);
    }
    if (disableToday) {
      return current.isAfter(today);
    }
    if (disablePastDate) {
      return current.isAfter(yesterday);
    }
    if (disableFutureDate) {
      return current.isBefore(today);
    }
    return customDisable(current);
  };

  const onBacktoToday = () => {
    const todayTemp = makeTodayMomentWithTz(timeZone).format(dateFormat);
    setTime(todayTemp);
    setisToday(true);
    setisPast(false);
  };

  const onPreviousDate = () => {
    const todayTemp = makeTodayMomentWithTz(timeZone).format(dateFormat);
    const yesterdayTemp = convertDateToMomentWithTz(time, dateFormat);
    yesterdayTemp.subtract(1, 'days');
    setTime(yesterdayTemp.format(dateFormat));
    setisToday(todayTemp === yesterdayTemp.format(dateFormat));
  };

  const onNextDate = () => {
    const tomorrow = convertDateToMomentWithTz(time, dateFormat);
    tomorrow.add(1, 'days');
    setTime(tomorrow.format(dateFormat));
  };

  return (
    <div className={clsx(classes.wrapper, 'wrap-daypicker')}>
      {leftItem && <div className={classes.leftItem}>{leftItem}</div>}
      <div
        className={
          // eslint-disable-next-line no-nested-ternary
          isPast && !isToday && isRangeTimeLabel
            ? 'past'
            : !isPast && !isToday && isRangeTimeLabel
            ? 'future'
            : ''
        }
        id='time-picker'
        ref={timePickerRef}
      >
        <Datetime
          {...props}
          popperClassName='date-picker-reports'
          className={clsx(classes.dateTimeContainer, className)}
          timeFormat={false}
          dateFormat={dateFormat}
          value={time}
          onChange={handleChangeDate}
          open={open}
          isValidDate={disableDate}
          displayTimeZone={timeZone}
          renderView={(mode: string, renderDefault: () => ReactNode) => (
            <Popover
              open={open}
              anchorEl={anchorEl}
              anchorOrigin={{
                horizontal: 'left',
                vertical: 'bottom',
              }}
              role={undefined}
              transition
              classes={{
                paper: classes.popover,
              }}
              onClose={() => {
                setAnchorEl(null);
              }}
              placement='bottom-start'
              modifiers={{
                offset: {
                  enabled: true,
                  offset: '0, 16',
                },
              }}
            >
              <div className={classes.wrapper} data-testid={paperDataTestId}>
                {renderDefault()}
              </div>
            </Popover>
          )}
          renderInput={(xProps: any) => (
            <TextInput
              data-testid={dataTestId}
              placeholder={placeholder}
              error={error}
              {...xProps}
              className={clsx(classes.pickupInput, xProps?.className)}
              value={time}
              onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                e.stopPropagation();
                e.preventDefault();
                if (!disabled) {
                  setAnchorEl((prev) =>
                    prev ? null : (e.currentTarget as HTMLElement),
                  );
                }
              }}
              InputProps={{
                inputProps: { onBlur, readOnly: true },
                endAdornment:
                  // eslint-disable-next-line no-nested-ternary
                  open && isDropDownIcon ? (
                    <ArrowDropUp
                      color='primary'
                      style={{ pointerEvents: 'none' }}
                    />
                  ) : onClear && time ? (
                    <IconButton
                      size='small'
                      className={classes.buttonClear}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClear();
                      }}
                    >
                      <Icon
                        name='close'
                        color={disabled ? 'rgba(0, 0, 0, 0.26)' : '#33363D '}
                        size={8}
                      />
                    </IconButton>
                  ) : (
                    isDropDownIcon && (
                      <KeyboardArrowDown
                        color={disabled ? 'disabled' : 'primary'}
                        style={{ pointerEvents: 'none' }}
                      />
                    )
                  ),
              }}
              disabled={disabled}
            />
          )}
        />
      </div>
      {isTodayButton && (
        <Button
          className='button-today'
          onClick={() => onBacktoToday()}
          disabled={isToday}
        >
          Today
        </Button>
      )}

      {isRangeTimeLabel && (
        <div
          className={`rangeDate ${
            isPast && !isToday
              ? 'past'
              : !isPast && !isToday
              ? 'future'
              : 'today-range'
          }`}
        >
          <div className='clock-range'>
            <svg
              id='Group_11771'
              xmlns='http://www.w3.org/2000/svg'
              width='32'
              height='32'
              viewBox='0 0 79.23 78.594'
            >
              <path
                id='Path_3418'
                d='M73.437,78.44H7a6.021,6.021,0,0,1-6-6V6A6.021,6.021,0,0,1,7,0H73.437a6.021,6.021,0,0,1,6,6V72.437A6.021,6.021,0,0,1,73.437,78.44Z'
                transform='translate(-0.21)'
                fill='transparent'
              />
              <rect
                id='Rectangle_2159'
                width='78.594'
                height='78.594'
                transform='translate(0)'
                fill='none'
              />
              <rect
                id='Rectangle_2160'
                width='78.594'
                height='78.594'
                transform='translate(0)'
                fill='none'
              />
              <g id='Group_7426' transform='translate(36.645 23.38)'>
                <path
                  className={`${isPast ? 'past' : 'future'}`}
                  id='Path_3419'
                  d='M100.651,85.472a2.391,2.391,0,0,1-1.7-.7L91.066,76.88a1.621,1.621,0,0,1-.476-1.145V60.92a2.467,2.467,0,0,1,2.325-2.5,2.4,2.4,0,0,1,2.477,2.4v13.6l6.956,6.956a2.4,2.4,0,0,1-1.7,4.1Z'
                  transform='translate(-90.59 -58.417)'
                />
              </g>
              <path
                className={`${isPast ? 'past' : 'future'}`}
                id='Path_3420'
                d='M74.464,55.455a25.381,25.381,0,0,0-50.278,4.526l-1.657-1.541a2.4,2.4,0,1,0-3.27,3.518l6.547,6.087a1.766,1.766,0,0,0,2.5-.092l6.019-6.475a2.47,2.47,0,0,0,.112-3.3,2.4,2.4,0,0,0-3.562-.044l-1.885,2.029a20.575,20.575,0,1,1,9.117,17.289,2.4,2.4,0,0,0-2.677,3.99A25.382,25.382,0,0,0,74.464,55.455Z'
                transform='translate(-10.702 -20.98)'
              />
            </svg>
          </div>

          <div className='text-range'>
            {isPast ? 'Past Date' : 'Future date'}
          </div>
        </div>
      )}
      {isNextDay && (
        <div className={classes.wrapNextDate}>
          <div
            onClick={() => onPreviousDate()}
            className='arrow-next'
            role='presentation'
          >
            <img src='/icons/arrowLeft.svg' alt='' />
          </div>
          <div
            onClick={() => onNextDate()}
            className='arrow-next right-arrow'
            role='presentation'
          >
            <img src='/icons/arrowLeft.svg' alt='' />
          </div>
        </div>
      )}
    </div>
  );
};

export default withStyles(themeStyle)(DayPicker);
